// ---------------------------------------------------------------------------
// DEV-3456: Finder-style multi-select math for list rows. Pure and UI-free --
// callers supply the on-screen row order and a grouping function; nothing here
// knows about candidate rows, imports or React.
// ---------------------------------------------------------------------------

import type { SelectedBlockPosition } from '@features/proposals/ElementListRow/ElementListRow';
import { assertUnreachable } from '@shared/helpers/function.helpers';

/** Group a row belongs to; a selection never spans two groups (= imports). */
export type GroupIdOf = (rowId: string) => string | undefined;

export type ClickResolutionInput = {
  /** Selectable row ids in on-screen order, group headers excluded. */
  orderedIds: readonly string[];
  selectedIds: ReadonlySet<string>;
  anchorId: string | undefined;
  targetId: string;
  shiftKey: boolean;
  metaOrCtrl: boolean;
  groupIdOf: GroupIdOf;
};

export type ClickResolution = {
  selectedIds: Set<string>;
  anchorId: string;
};

/**
 * Inclusive slice of `orderedIds` between two rows, order-independent. Rows
 * outside the anchor's group are dropped, which clamps the range to one import
 * without the caller having to pre-slice the id list.
 */
export const selectRange = (
  orderedIds: readonly string[],
  anchorId: string,
  targetId: string,
  groupIdOf: GroupIdOf,
): Set<string> => {
  const anchorIndex = orderedIds.indexOf(anchorId);
  const targetIndex = orderedIds.indexOf(targetId);
  if (anchorIndex < 0 || targetIndex < 0) return new Set([targetId]);

  const from = Math.min(anchorIndex, targetIndex);
  const to = Math.max(anchorIndex, targetIndex);
  const group = groupIdOf(anchorId);

  return new Set(
    orderedIds.slice(from, to + 1).filter((id) => groupIdOf(id) === group),
  );
};

const toggle = (selectedIds: ReadonlySet<string>, id: string): Set<string> => {
  const next = new Set(selectedIds);
  if (!next.delete(id)) next.add(id);
  return next;
};

/**
 * Finder semantics:
 * - Plain click → only that row (clears the rest)
 * - Cmd/Ctrl+click → toggle that row in/out without touching neighbours
 * - Shift+click → inclusive range from the anchor (includes rows in between)
 * - Cmd/Ctrl+A → every visible row in the active import (see
 *   {@link resolveSelectAllVisible})
 * - Cmd/Ctrl+Shift+Arrow → from the active row to the top/bottom of that
 *   import (see {@link resolveSelectToEdge})
 *
 * A modified click into another group starts a fresh selection there rather
 * than mixing two imports.
 */
export const resolveClickSelection = ({
  orderedIds,
  selectedIds,
  anchorId,
  targetId,
  shiftKey,
  metaOrCtrl,
  groupIdOf,
}: ClickResolutionInput): ClickResolution => {
  const single: ClickResolution = {
    selectedIds: new Set([targetId]),
    anchorId: targetId,
  };
  if (!shiftKey && !metaOrCtrl) return single;

  const reference = shiftKey ? (anchorId ?? targetId) : anchorId;
  const crossesGroups =
    reference !== undefined &&
    groupIdOf(reference) !== undefined &&
    groupIdOf(reference) !== groupIdOf(targetId);
  if (crossesGroups) return single;

  if (shiftKey) {
    const anchor = anchorId ?? targetId;
    return {
      selectedIds: selectRange(orderedIds, anchor, targetId, groupIdOf),
      anchorId: anchor,
    };
  }

  return { selectedIds: toggle(selectedIds, targetId), anchorId: targetId };
};

export type SelectToEdgeDirection = 'up' | 'down';

/**
 * Cmd+Shift+Arrow: from {@link fromId} through the first/last visible row in
 * the same import. Anchor stays on {@link fromId} so a later Shift+click can
 * still measure from the start of the gesture. Never spans two imports.
 */
export const resolveSelectToEdge = (
  orderedIds: readonly string[],
  fromId: string,
  direction: SelectToEdgeDirection,
  groupIdOf: GroupIdOf,
): (ClickResolution & { edgeId: string }) | undefined => {
  const fromIndex = orderedIds.indexOf(fromId);
  if (fromIndex < 0) return undefined;

  const group = groupIdOf(fromId);
  let edgeId = fromId;
  if (direction === 'down') {
    for (let i = fromIndex; i < orderedIds.length; i++) {
      const id = orderedIds[i];
      if (id === undefined || groupIdOf(id) !== group) break;
      edgeId = id;
    }
  } else {
    for (let i = fromIndex; i >= 0; i--) {
      const id = orderedIds[i];
      if (id === undefined || groupIdOf(id) !== group) break;
      edgeId = id;
    }
  }

  return {
    selectedIds: selectRange(orderedIds, fromId, edgeId, groupIdOf),
    anchorId: fromId,
    edgeId,
  };
};

/**
 * Cmd/Ctrl+A: every currently visible row in the same import as
 * {@link referenceId}. Falls back to the first visible row's import when there
 * is no reference (nothing focused yet). Never spans two imports.
 */
export const resolveSelectAllVisible = (
  orderedIds: readonly string[],
  referenceId: string | undefined,
  groupIdOf: GroupIdOf,
): ClickResolution | undefined => {
  if (orderedIds.length === 0) return undefined;

  const referenceGroup =
    referenceId !== undefined ? groupIdOf(referenceId) : undefined;
  const group =
    referenceGroup ??
    (orderedIds[0] !== undefined ? groupIdOf(orderedIds[0]) : undefined);

  const ids =
    group === undefined
      ? [...orderedIds]
      : orderedIds.filter((id) => groupIdOf(id) === group);
  if (ids.length === 0) return undefined;

  const firstId = ids[0];
  if (firstId === undefined) return undefined;

  const anchorId =
    referenceId !== undefined && ids.includes(referenceId)
      ? referenceId
      : firstId;

  return { selectedIds: new Set(ids), anchorId };
};

/**
 * Selected ids that are currently on screen (search and/or status filter).
 */
export const intersectSelectedWithVisible = (
  selectedIds: ReadonlySet<string>,
  visibleRowIds: ReadonlySet<string>,
): Set<string> => {
  const visible = new Set<string>();
  for (const id of selectedIds) {
    if (visibleRowIds.has(id)) visible.add(id);
  }
  return visible;
};

/**
 * Row ids that should receive an action triggered on `rowId` when a
 * multi-selection is active (DEV-3456). Hidden rows are dropped so Skip,
 * Backspace, paste, and conversion never mutate off-screen rows. Single-row
 * when `rowId` is outside the visible selection or that selection is one row.
 * Empty when the trigger itself is hidden.
 */
export const resolveBulkActionTargetIds = (
  rowId: string,
  selectedIds: ReadonlySet<string>,
  visibleRowIds: ReadonlySet<string>,
): string[] => {
  const visibleSelected = intersectSelectedWithVisible(
    selectedIds,
    visibleRowIds,
  );
  if (visibleSelected.size > 1 && visibleSelected.has(rowId)) {
    return [...visibleSelected];
  }
  return visibleRowIds.has(rowId) ? [rowId] : [];
};

/**
 * Border collapsing: adjacency is read off the *rendered* order, so a row
 * hidden by a filter splits the block visually even though it stays selected.
 */
export const getSelectedBlockPosition = (
  renderedIds: readonly string[],
  selectedIds: ReadonlySet<string>,
  rowId: string,
): SelectedBlockPosition => {
  const index = renderedIds.indexOf(rowId);
  if (index < 0) return 'single';

  const prevId = renderedIds[index - 1];
  const nextId = renderedIds[index + 1];
  const prevSelected = prevId !== undefined && selectedIds.has(prevId);
  const nextSelected = nextId !== undefined && selectedIds.has(nextId);

  if (prevSelected && nextSelected) return 'middle';
  if (prevSelected) return 'last';
  if (nextSelected) return 'first';
  return 'single';
};

/**
 * The file-header divider is the visual top edge of the first rendered row.
 * Drop that row's own top border so the selection line is not doubled.
 */
export const selectedBlockPositionBelowFileHeader = (
  position: SelectedBlockPosition,
  isFirstRendered: boolean,
): SelectedBlockPosition => {
  if (!isFirstRendered) return position;
  switch (position) {
    case 'single':
      return 'last';
    case 'first':
      return 'middle';
    case 'middle':
    case 'last':
      return position;
    default:
      return assertUnreachable(position);
  }
};
